import React, { useMemo, useRef, useState } from 'react';

import {
  ensureStableTableIds,
  TABLE_ID_PATTERN,
} from './bookTableAnchor';
import { normalizeTableShape } from './contentLayout';
import TableEditor from './TableEditor';

const clampInteger = (value, minimum, maximum) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return minimum;
  return Math.max(minimum, Math.min(maximum, parsed));
};

const createEmptyTable = () =>
  normalizeTableShape({
    title: '',
    rows: 2,
    columns: 2,
    grid: [
      ['', ''],
      ['', ''],
    ],
    content: ' | \n | ',
  });

const resizeRows = (table, requestedRows) => {
  const shaped = normalizeTableShape(table);
  const rows = clampInteger(requestedRows, 1, 30);
  const grid = Array.from({ length: rows }, (_, rowIndex) =>
    Array.from(
      { length: shaped.columns },
      (_, columnIndex) => shaped.grid[rowIndex]?.[columnIndex] ?? ''
    )
  );
  return normalizeTableShape({ ...shaped, rows, grid });
};

const VisualTableListEditor = ({
  tables = [],
  onChange,
  onValidityChange,
  onDeleteTable,
}) => {
  const [dimensionDrafts, setDimensionDrafts] = useState({});
  const validityByTableId = useRef(new Map());
  const anonymousTableIds = useRef([]);
  const shapedTables = useMemo(
    () => {
      const availableIds = new Map();
      anonymousTableIds.current.forEach(({ signature, id }) => {
        const ids = availableIds.get(signature) || [];
        ids.push(id);
        availableIds.set(signature, ids);
      });
      const usedIds = new Set();
      const nextAnonymousTableIds = [];
      const nextTables = (Array.isArray(tables) ? tables : []).map(
        (table) => {
          const shaped = normalizeTableShape(table);
          let tableId = shaped.id;
          if (!TABLE_ID_PATTERN.test(tableId || '') || usedIds.has(tableId)) {
            const signature = JSON.stringify({ ...shaped, id: undefined });
            const reusableIds = availableIds.get(signature) || [];
            tableId = reusableIds.find((id) => !usedIds.has(id));
            while (!tableId || usedIds.has(tableId)) {
              tableId = ensureStableTableIds([{ ...shaped, id: undefined }])[0]
                .id;
            }
            nextAnonymousTableIds.push({ signature, id: tableId });
          }
          usedIds.add(tableId);
          return { ...shaped, id: tableId };
        }
      );
      anonymousTableIds.current = nextAnonymousTableIds;
      return nextTables;
    },
    [tables]
  );

  const emit = (nextTables) => {
    onChange?.(
      ensureStableTableIds(
        nextTables.map((table) => normalizeTableShape(table))
      )
    );
  };

  const replaceTable = (tableIndex, nextTable) => {
    emit(
      shapedTables.map((table, index) =>
        index === tableIndex ? nextTable : table
      )
    );
  };

  const dimensionKey = (table, dimension) => `${table.id}:${dimension}`;

  const updateDimensionDraft = (key, value) => {
    setDimensionDrafts((current) => ({ ...current, [key]: value }));
  };

  const commitDimension = (table, tableIndex, dimension) => {
    const key = dimensionKey(table, dimension);
    const draftValue = dimensionDrafts[key];
    setDimensionDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    if (draftValue === undefined || String(draftValue).trim() === '') return;

    if (dimension === 'rows') {
      replaceTable(tableIndex, resizeRows(table, draftValue));
      return;
    }
    replaceTable(
      tableIndex,
      normalizeTableShape(table, clampInteger(draftValue, 1, 12))
    );
  };

  return (
    <div className="grid min-w-0 gap-3">
      {shapedTables.map((table, tableIndex) => {
        const rowsKey = dimensionKey(table, 'rows');
        const columnsKey = dimensionKey(table, 'columns');
        return (
          <section
            key={table.id}
            role="region"
            aria-label={`Tableau ${tableIndex + 1}`}
            className="grid min-w-0 gap-3 rounded border border-gray-200 bg-white p-3"
          >
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="grid gap-1 text-sm">
              <span>Intitulé du tableau</span>
              <input
                type="text"
                aria-label={`Titre du tableau ${tableIndex + 1}`}
                className="rounded border border-gray-300 px-2 py-1"
                value={table.title || ''}
                onChange={(event) =>
                  replaceTable(tableIndex, {
                    ...table,
                    title: event.target.value,
                  })
                }
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>Lignes</span>
              <input
                type="number"
                min="1"
                max="30"
                aria-label={`Nombre de lignes du tableau ${tableIndex + 1}`}
                className="rounded border border-gray-300 px-2 py-1"
                value={dimensionDrafts[rowsKey] ?? String(table.rows)}
                onChange={(event) =>
                  updateDimensionDraft(rowsKey, event.target.value)
                }
                onBlur={() => commitDimension(table, tableIndex, 'rows')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>Colonnes</span>
              <input
                type="number"
                min="1"
                max="12"
                aria-label={`Nombre de colonnes du tableau ${tableIndex + 1}`}
                className="rounded border border-gray-300 px-2 py-1"
                value={dimensionDrafts[columnsKey] ?? String(table.columns)}
                onChange={(event) =>
                  updateDimensionDraft(columnsKey, event.target.value)
                }
                onBlur={() => commitDimension(table, tableIndex, 'columns')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
              />
            </label>
          </div>

          <TableEditor
            table={table}
            onChange={(nextTable) => replaceTable(tableIndex, nextTable)}
            onValidityChange={(isValid) => {
              validityByTableId.current.set(table.id, isValid);
              onValidityChange?.(tableIndex, isValid);
            }}
          />

          <button
            type="button"
            aria-label={`Supprimer le tableau ${tableIndex + 1}`}
            className="w-fit rounded bg-red-600 px-3 py-1 text-sm text-white"
            onClick={() => {
              const nextTables = shapedTables.filter(
                (_, index) => index !== tableIndex
              );
              onDeleteTable?.(table.id);
              shapedTables.forEach((_, index) =>
                onValidityChange?.(index, true)
              );
              validityByTableId.current.delete(table.id);
              nextTables.forEach((nextTable, nextIndex) =>
                onValidityChange?.(
                  nextIndex,
                  validityByTableId.current.get(nextTable.id) ?? true
                )
              );
              setDimensionDrafts((current) =>
                Object.fromEntries(
                  Object.entries(current).filter(
                    ([key]) => !key.startsWith(`${table.id}:`)
                  )
                )
              );
              emit(nextTables);
            }}
          >
            Supprimer le tableau
          </button>
          </section>
        );
      })}

      <button
        type="button"
        className="w-fit rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
        onClick={() => emit([...shapedTables, createEmptyTable()])}
      >
        Ajouter un tableau
      </button>
    </div>
  );
};

export default VisualTableListEditor;
